import { Decimal } from 'decimal.js';
import type { Currency, Money } from '@/lib/api/types';

// All money math goes through decimal.js; amounts never become JS numbers.
export const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
export type Dec = InstanceType<typeof D>;

/** IQD has no fils in use; LYD uses dirhams (1/1000). */
export const CURRENCY_DECIMALS: Record<Currency, number> = { IQD: 0, LYD: 3 };

export const ZERO: Dec = new D(0);

export function toDec(value: Money | Dec | number | null | undefined): Dec {
  if (value === null || value === undefined || value === '') return ZERO;
  if (value instanceof D) return value;
  try {
    return new D(value);
  } catch {
    return ZERO;
  }
}

export function sumMoney(values: Iterable<Money | Dec | null | undefined>): Dec {
  let total = ZERO;
  for (const v of values) total = total.plus(toDec(v));
  return total;
}

/** Wire format: a plain decimal string with three places. */
export function toWire(value: Dec | Money): Money {
  return toDec(value).toFixed(3);
}

export function currencySymbol(currency: Currency, lang: 'ar' | 'en'): string {
  if (lang === 'ar') return currency === 'IQD' ? 'د.ع' : 'د.ل';
  return currency;
}

/**
 * The locale whose number rules a school's locale uses. Libya writes 1,250.500 (comma grouping, dot before the
 * dirhams, Latin digits), not ICU's ar-LY "1.250,500", which Libyans read as a thousand times larger.
 */
export function numberLocale(locale: string): string {
  return locale === 'ar-LY' ? 'en-US' : locale;
}

const formatters = new Map<string, Intl.NumberFormat>();

export function numberFormat(locale: string, minFraction: number, maxFraction = minFraction): Intl.NumberFormat {
  const key = `${locale}|${minFraction}|${maxFraction}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat(numberLocale(locale), { minimumFractionDigits: minFraction, maximumFractionDigits: maxFraction });
    formatters.set(key, f);
  }
  return f;
}

export interface MoneyFormatOptions {
  /** Append the currency symbol (default true). */
  symbol?: boolean;
  /** Show "+" for positive amounts (differences). */
  signed?: boolean;
}

/**
 * Formats a decimal string with the school's locale: ar-IQ → "١٬٢٥٠٬٠٠٠ د.ع",
 * ar-LY → "1,250,000.125 د.ل", en → "1,250,000 IQD". The string goes to
 * Intl as a string, so no precision is lost on the way.
 */
export function formatMoney(
  value: Money | Dec | null | undefined,
  ctx: { locale: string; currency: Currency; lang: 'ar' | 'en' },
  opts: MoneyFormatOptions = {},
): string {
  const dp = CURRENCY_DECIMALS[ctx.currency];
  const d = toDec(value).toDecimalPlaces(dp);
  let text = numberFormat(ctx.locale, dp).format(d.toFixed(dp) as `${number}`);
  if (opts.signed && d.greaterThan(0)) text = `+${text}`;
  return opts.symbol === false ? text : `${text} ${currencySymbol(ctx.currency, ctx.lang)}`;
}

export function formatNumber(value: number | Money, locale: string, maxFraction = 0): string {
  return numberFormat(locale, 0, maxFraction).format(value as number);
}

const ARABIC_INDIC = /[٠-٩]/g;
const EXTENDED_ARABIC_INDIC = /[۰-۹]/g;

/** Arabic-Indic and Persian digits → ASCII; Arabic decimal separator → "." */
export function normalizeDigits(input: string): string {
  return input
    .replace(ARABIC_INDIC, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(EXTENDED_ARABIC_INDIC, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/٫/g, '.');
}

export type MoneyInputError = 'required' | 'invalid' | 'decimals' | 'positive';

/**
 * Which separator in a typed amount is the decimal point. People type
 * "1,250,000", "1,250.5" (how amounts are shown), "1.250.000" or "1.250,5"
 * (ICU's ar-LY style, still common), "12,5", "١٢٫٥" (Arabic decimal separator):
 * - two different separators: the last one is the decimal point;
 * - one kind repeated: grouping;
 * - a single one followed by exactly three digits: grouping for IQD (no
 *   decimals); for LYD (three decimals) a comma groups ("1,500" is fifteen
 *   hundred, as shown) and a dot is the decimal point ("1.500" is one and a half);
 * - otherwise: the decimal point.
 * MoneyInput echoes the parsed amount (in words in Arabic) whenever a
 * separator was typed, so an unexpected reading is visible before saving.
 */
function normalizeSeparators(s: string, currency: Currency): string {
  const seps = s.match(/[.,]/g);
  if (!seps) return s;
  const last = Math.max(s.lastIndexOf('.'), s.lastIndexOf(','));
  const lastSep = s[last];
  const mixed = seps.some((c) => c !== lastSep);
  const repeated = seps.length > 1;
  const after = s.length - last - 1;
  const grouping3 = after === 3 && (CURRENCY_DECIMALS[currency] === 0 || lastSep === ',');
  const decimal = mixed || (!repeated && !grouping3);
  return decimal ? `${s.slice(0, last).replace(/[.,]/g, '')}.${s.slice(last + 1)}` : s.replace(/[.,]/g, '');
}

/**
 * Parses what a person typed into a money field. Accepts Arabic-Indic digits,
 * Arabic separators and spaces; see normalizeSeparators for "." and ",".
 */
export function parseMoneyInput(
  raw: string,
  currency: Currency,
  opts: { allowZero?: boolean } = {},
): { value: Money | null; error: MoneyInputError | null } {
  const typed = normalizeDigits(raw)
    .replace(/\u066C/g, ',')
    .replace(/[\s\u00A0\u202F']/g, '');
  if (!typed) return { value: null, error: 'required' };
  if (!/^\d(?:[\d.,]*\d)?$/.test(typed)) return { value: null, error: 'invalid' };
  const cleaned = normalizeSeparators(typed, currency);
  if (!/^\d{1,15}(\.\d+)?$/.test(cleaned)) return { value: null, error: 'invalid' };
  const fraction = cleaned.split('.')[1] ?? '';
  if (fraction.replace(/0+$/, '').length > CURRENCY_DECIMALS[currency]) return { value: null, error: 'decimals' };
  const d = new D(cleaned);
  if (!opts.allowZero && !d.greaterThan(0)) return { value: null, error: 'positive' };
  return { value: d.toFixed(3), error: null };
}

/** True when the typed text has a separator whose meaning the person should see confirmed. */
export function hasSeparator(raw: string): boolean {
  return /[.,\u066B\u066C]/.test(raw);
}

/** The plain editable text for an amount (no grouping, ASCII digits, trimmed decimals). */
export function moneyInputText(value: Money | Dec | null | undefined, currency: Currency): string {
  if (value === null || value === undefined || value === '') return '';
  return toDec(value).toDecimalPlaces(CURRENCY_DECIMALS[currency]).toFixed(CURRENCY_DECIMALS[currency]);
}

/**
 * Mirrors the server's split (shared/money.ts): parts rounded up to `unit`,
 * the smaller remainder on the last part. Used for previews only; the server
 * computes the real schedule.
 */
export function splitEvenly(total: Dec, count: number, unit: Dec): Dec[] {
  if (!Number.isInteger(count) || count < 1 || total.isNegative()) return [];
  const step = unit.isZero() ? new D('0.001') : unit;
  let part = total.dividedBy(count).dividedBy(step).ceil().times(step);
  if (part.times(count - 1).greaterThan(total)) part = total.dividedBy(count).dividedBy(step).floor().times(step);
  const parts: Dec[] = Array.from({ length: count }, () => part);
  parts[count - 1] = total.minus(part.times(count - 1));
  return parts;
}
